import { apiInstanceFetch } from "../../util/api";
import { apiInstance } from "../../util/api-new";
import { setToast } from "../../util/toast";
import * as ActionType from "./course.type";

// Get organizations list for the course access picker (paginated + searchable)
export const getAllOrganizations = async (query, user = "org") => {
  const queryParams = new URLSearchParams(query).toString();

  return user === "admin"
    ? apiInstanceFetch
        .get(`admin/organization?${queryParams}`)
        .then((res) => ({
          organizations: res.data?.organizations || [],
          total: res.data?.totalOrganizations || res.data?.total || 0,
        }))
        .catch((error) => {
          console.error("Failed to fetch organizations", error);
          throw error;
        })
    : apiInstance
        .get(`organization/all?${queryParams}`)
        .then((res) => ({
          organizations: res.data?.organizations || [],
          total: res.data?.totalOrganizations || res.data?.total || 0,
        }))
        .catch((error) => {
          console.error("Failed to fetch organizations", error);
          throw error;
        });
};

// Update the list of organizations a course is accessible to
export const updateCourseAccess = (courseId, accessTo, user = "org") => (dispatch) => {
  return user === "admin"
    ? apiInstanceFetch
        .patch(`admin/course/${courseId}/access`, { accessTo })
        .then((res) => {
          if (res.status) {
            dispatch({
              type: ActionType.UPDATE_COURSE_ACCESS,
              payload: {
                courseId,
                updatedData: { accessTo: res.data || accessTo },
              },
            });
            setToast("success", res.message || "Course access updated!");
          }
          return res.status;
        })
        .catch((error) => {
          console.error("Failed to update course access", error);
          setToast("error", "Failed to update course access");
          throw error;
        })
    : apiInstance
        .patch(`organization/course/${courseId}/access`, { accessTo })
        .then((res) => {
          if (res.status) {
            dispatch({
              type: ActionType.UPDATE_COURSE_ACCESS,
              payload: {
                courseId,
                updatedData: { accessTo: res.data || accessTo },
              },
            });
            setToast("success", res.message || "Course access updated!");
          }
          return res.status;
        })
        .catch((error) => {
          console.error("Failed to update course access", error);
          setToast("error", "Failed to update course access");
          throw error;
        });
};

// Organization Course Actions
export const getOrgCourses =
  (page = 1, limit = 10, search) =>
  (dispatch) => {
    apiInstance
      .get(
        `organization/course?page=${page}&limit=${limit}&searchText=${search}`,
      )
      .then((res) => {
        dispatch({
          type: ActionType.GET_COURSES,
          payload: {
            course: res.data?.courses,
            totalCourses: res.data?.totalCourses,
          },
        });
      });
  };

export const searchAdminCourses = async (searchText, currentCourseId) => {
  return await apiInstanceFetch
    .get(
      `admin/course?limit=10&searchType=prerequisite&searchText=${searchText}&excludeCourseId=${currentCourseId}`,
    )
    .then((res) => res.data.courses)
    .catch((error) => {
      console.error("Failed to search courses", error);
      throw error;
    });
};

export const searchCourses = async (searchText, currentCourseId) => {
  return await apiInstanceFetch
    .get(
      `organization/course?limit=10&searchType=prerequisite&searchText=${searchText}&excludeCourseId=${currentCourseId}`,
    )
    .then((res) => res.data.courses)
    .catch((error) => {
      console.error("Failed to search courses", error);
      throw error;
    });
};

export const addOrgCourse = (courseData) => (dispatch) => {
  apiInstance.post("organization/course/create", courseData).then((res) => {
    if (res.status) {
      dispatch({
        type: ActionType.ADD_COURSE,
        payload: res.data,
      });
    }
  });
};

export const updateOrgCourse = (courseId, updatedData) => (dispatch) => {
  // Simulate API call to update organization course
  apiInstance
    .put(`organization/course/${courseId}`, updatedData)
    .then((res) => {
      if (res.status) {
        dispatch({
          type: ActionType.UPDATE_COURSE,
          payload: {
            courseId,
            updatedData: res.data,
          },
        });
      }
    });
};

export const deleteOrgCourse = (courseId) => (dispatch) => {
  apiInstance.delete(`organization/course/${courseId}`).then((res) => {
    if (res.status) {
      dispatch({
        type: ActionType.DELETE_COURSE,
        payload: courseId,
      });
    }
  });
};

// Organization Lessons actions
export const addOrgLesson = (courseId, lessonData) => async (dispatch) => {
  return apiInstance
    .post(`organization/course/${courseId}/lesson`, lessonData)
    .then((res) => {
      if (res.status) {
        dispatch({
          type: ActionType.ADD_LESSON,
          payload: { courseId, lessonData: res.lesson },
        });
        return res.lesson;
      }
    });
};

export const updateOrgLesson =
  (courseId, lessonId, updatedData) => (dispatch) => {
    apiInstance
      .put(`organization/lesson/${lessonId}`, updatedData)
      .then((res) => {
        if (res.status) {
          dispatch({
            type: ActionType.UPDATE_LESSON,
            payload: { courseId, lessonId, updatedData: res.lesson },
          });
        }
      });
  };

export const deleteOrgLesson = (courseId, lessonId) => (dispatch) => {
  apiInstance.delete(`organization/lesson/${lessonId}`).then((res) => {
    if (res.status) {
      dispatch({
        type: ActionType.DELETE_LESSON,
        payload: { courseId, lessonId },
      });
    }
  });
};

export const addOrgQuizQuestion =
  (courseId, lessonId, quizData) => (dispatch) => {
    apiInstance
      .post(`organization/lesson/${lessonId}/quiz`, { question: quizData })
      .then((res) => {
        if (res.status) {
          dispatch({
            type: ActionType.CREATE_QUIZ,
            payload: { courseId, lessonId, initialQuizData: res.quiz },
          });
        }
      });
  };

export const updateOrgQuizPassMarks =
  (courseId, lessonId, quizId, passMark) => (dispatch) => {
    apiInstance.put(`organization/quiz/${quizId}`, { passMark }).then((res) => {
      if (res.status) {
        dispatch({
          type: ActionType.UPDATE_QUIZ_QUESTION,
          payload: { courseId, lessonId, updatedQuizData: res.quiz },
        });
      }
    });
  };

export const updateOrgQuizQuestion =
  (courseId, lessonId, updatedQuizData) => (dispatch) => {
    apiInstance
      .put(`organization/quiz/${updatedQuizData._id}`, {
        questions: updatedQuizData.questions,
      })
      .then((res) => {
        if (res.status) {
          dispatch({
            type: ActionType.UPDATE_QUIZ_QUESTION,
            payload: {
              courseId,
              lessonId,
              updatedQuizData: res.quiz,
            },
          });
        }
      });
  };

export const deleteOrgQuizQuestion =
  (courseId, lessonId, updatedQuizDel) => (dispatch) => {
    apiInstance
      .put(`organization/quiz/${updatedQuizDel._id}`, {
        questions: updatedQuizDel.questions,
      })
      .then((res) => {
        if (res.status) {
          dispatch({
            type: ActionType.DELETE_QUIZ_QUESTION,
            payload: { courseId, lessonId, updatedQuiz: res.quiz },
          });
        }
      });
  };

// Action to toggle the publish status of a course
export const toggleOrgPublishStatus = (courseId) => (dispatch) => {
  apiInstance
    .patch(`organization/course/${courseId}/toggle-publish`)
    .then((res) => {
      if (res.status) {
        dispatch({
          type: ActionType.TOGGLE_PUBLISH_STATUS,
          payload: { courseId },
        });
        setToast("success", res.message);
      } else {
        setToast("error", res.error);
      }
    })
    .catch((error) => {
      console.error("Failed to toggle publish status", error);
      throw error;
    });
};

// Get all videos for lesson - returns a Promise with fetched data
export const getOrgVideosForLesson = async (searchTitle = "", courseId) => {
  const encodedSearch = encodeURIComponent(searchTitle || "");

  return apiInstance
    .get(
      `organization/videos/available?searchText=${encodedSearch}&courseId=${courseId}`,
    )
    .then((res) => res.videos)
    .catch((error) => {
      console.error("Failed to fetch videos for lessons", error);
      throw error;
    });
};

// Action to set initial course data
export const getCourses =
  (page, limit, publishType, searchText) => (dispatch) => {
    apiInstanceFetch
      .get(
        `admin/course?page=${page}&limit=${limit}&publishType=${publishType}&searchText=${searchText}`,
      )
      .then((res) => {
        dispatch({
          type: ActionType.GET_COURSES,
          payload: {
            course: res.data.courses,
            totalCourse: res.data.totalCourses,
          },
        });
      });
  };

// Action to add a new course
export const addCourse = (courseData) => (dispatch) => {
  // TODO : check price and publish type and then update redux
  apiInstanceFetch.post("admin/course", courseData).then((res) => {
    dispatch({
      type: ActionType.ADD_COURSE,
      payload: res.data,
    });
  });
};

// Action to update a course
export const updateCourse = (courseId, updatedData) => (dispatch) => {
  apiInstanceFetch.put(`admin/course/${courseId}`, updatedData).then((res) => {
    dispatch({
      type: ActionType.UPDATE_COURSE,
      payload: { courseId, updatedData },
    });
  });
};

// Action to delete a course
export const deleteCourse = (courseId) => (dispatch) => {
  apiInstanceFetch.delete(`admin/course/${courseId}`).then((res) => {
    if (res.status) {
      dispatch({
        type: ActionType.DELETE_COURSE,
        payload: courseId,
      });
    }
  });
};

// Action to add a lesson to a specific course
// FUTURE CHANGE : For api response to function return like this
export const addLesson = (courseId, lessonData) => async (dispatch) => {
  return apiInstanceFetch
    .post(`admin/course/${courseId}/lesson`, lessonData)
    .then((res) => {
      if (res.status) {
        dispatch({
          type: ActionType.ADD_LESSON,
          payload: { courseId, lessonData: res.lesson },
        });
      }
      return res.lesson;
    });
};

// Action to update a lesson in a specific course
export const updateLesson = (courseId, lessonId, updatedData) => (dispatch) => {
  apiInstanceFetch.put(`admin/lesson/${lessonId}`, updatedData).then((res) => {
    if (res.status) {
      dispatch({
        type: ActionType.UPDATE_LESSON,
        payload: { courseId, lessonId, updatedData: res.data.lesson },
      });
    }
  });
};

// Action to delete a lesson from a specific course
export const deleteLesson = (courseId, lessonId) => (dispatch) => {
  apiInstanceFetch.delete(`admin/lesson/${lessonId}`).then((res) => {
    if (res.status) {
      dispatch({
        type: ActionType.DELETE_LESSON,
        payload: { courseId, lessonId },
      });
    } else {
      setToast("error", res.error);
    }
  });
};

// Action to select a course, No API required; Only redux purpose
export const selectCourse = (course) => (dispatch) => {
  dispatch({
    type: ActionType.SELECT_COURSE,
    payload: course,
  });
};

// Action to add a quiz question to a specific lesson
export const addQuizQuestion = (courseId, lessonId, quizData) => (dispatch) => {
  apiInstanceFetch
    .post(`admin/lesson/${lessonId}/quiz`, { question: quizData })
    .then((res) => {
      if (res.status) {
        dispatch({
          type: ActionType.CREATE_QUIZ,
          payload: { courseId, lessonId, initialQuizData: res.quiz },
        });
      }
    });
};

// Action to update quiz pass marks
export const updateQuizPassMarks =
  (courseId, lessonId, quizId, passMark) => (dispatch) => {
    apiInstanceFetch.put(`admin/quiz/${quizId}`, { passMark }).then((res) => {
      if (res.status) {
        dispatch({
          type: ActionType.UPDATE_QUIZ_QUESTION,
          payload: { courseId, lessonId, updatedQuizData: res.quiz },
        });
      }
    });
  };

// Action to update a quiz question in a specific lesson
export const updateQuizQuestion =
  (courseId, lessonId, updatedQuizData) => (dispatch) => {
    apiInstanceFetch
      .put(`admin/quiz/${updatedQuizData._id}`, {
        questions: updatedQuizData.questions,
      })
      .then((res) => {
        if (res.status) {
          dispatch({
            type: ActionType.UPDATE_QUIZ_QUESTION,
            payload: {
              courseId,
              lessonId,
              updatedQuizData: res.quiz,
            },
          });
        }
      });
  };

// Action to delete a quiz question from a specific lesson
export const deleteQuizQuestion =
  (courseId, lessonId, updatedQuizDel) => (dispatch) => {
    apiInstanceFetch
      .put(`admin/quiz/${updatedQuizDel._id}`, {
        questions: updatedQuizDel.questions,
      })
      .then((res) => {
        if (res.status) {
          dispatch({
            type: ActionType.DELETE_QUIZ_QUESTION,
            payload: { courseId, lessonId, updatedQuiz: res.quiz },
          });
        }
      });
  };

// Action to toggle the publish status of a course
export const togglePublishStatus = (courseId) => (dispatch) => {
  apiInstanceFetch
    .patch(`admin/course/${courseId}/toggle-publish`)
    .then((res) => {
      if (res.status) {
        dispatch({
          type: ActionType.TOGGLE_PUBLISH_STATUS,
          payload: { courseId },
        });
      } else {
        setToast("error", res.error);
      }
    })
    .catch((error) => {
      console.error("Failed to toggle publish status", error);
      throw error;
    });
};

// Get all videos for lesson - returns a Promise with fetched data
export const getVideosForLesson = async (searchTitle = "", language) => {
  const encodedSearch = encodeURIComponent(searchTitle || "");

  return apiInstanceFetch
    .get(
      `admin/video/videosForLessons?searchTitle=${encodedSearch}&language=${language}`,
    )
    .then((res) => res.videos)
    .catch((error) => {
      console.error("Failed to fetch videos for lessons", error);
      throw error;
    });
};

// Get enrolled users for a course
export const getEnrolledUsers = (courseId, query) => {
  const queryParams = new URLSearchParams(query).toString();

  return apiInstanceFetch
    .get(`admin/course/${courseId}/enrollments?${queryParams}`)
    .then((res) => res)
    .catch((error) => {
      console.error("Failed to fetch enrolled users", error);
      throw error;
    });
};
